import React from 'react';
import { DirectorReportView } from './creditDirectorReportsTypes';
import { formatCurrency } from '../../../utils/formatters';

const scoringVariableDescriptions: Record<string, string> = {
    xds_presage_v4_score: 'Credit bureau score',
    existing_customer: 'Existing Tirhelo customer',
    ltc_pct_satisfactory_accounts: '% of accounts in good standing',
    usc_age_oldest_open_satisfactory_months: 'Age (months) successfully managed unsecured account',
    ltc_total_balance_open_excl_homeloan: 'Sum of outstanding debt excl homeloan accounts',
    usc_avg_instalment_open: 'Avg instalment amount on unsecured',
    per_ratio_industry_opening_bal_to_all: 'How much of the borrowing is personal loans',
    all_ratio_balance_to_opening_open_active: 'Pay down velocity - lower is good'
};

const escapedVariableNames = Object.keys(scoringVariableDescriptions).map((name) =>
    name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
);

const scoringVariablePattern = new RegExp(`\\b(${escapedVariableNames.join('|')})\\b`, 'g');

const normalizeKnownAcronyms = (value: string): string => {
    return value
        .replace(/\bC\s*P\s*A\b/gi, 'CPA')
        .replace(/\bN\s*L\s*R\b/gi, 'NLR');
};

const toVariableDescription = (value: string): string => {
    const variableDescription = scoringVariableDescriptions[value];
    if (variableDescription) {
        return variableDescription;
    }

    return value.replace(scoringVariablePattern, (match) => scoringVariableDescriptions[match] || match);
};

export const toRecord = (value: unknown): Record<string, unknown> | null => {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return null;
    }
    return value as Record<string, unknown>;
};

const toRecordArray = (value: unknown): Record<string, unknown>[] => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value
        .map((item) => toRecord(item))
        .filter((item): item is Record<string, unknown> => item !== null);
};

const moneyFieldPattern = /(Amt|Amount|Balance|Debt|Price|Income|Expenses|Commitments|Instalment|Installment)/i;
const explicitMoneyFields = new Set([
    'ltc_total_balance_open_excl_homeloan',
    'usc_avg_instalment_open'
]);

const toNumberValue = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
            return null;
        }

        const parsed = Number(trimmed.replace(/,/g, ''));
        return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
};

const shouldFormatAsCurrency = (key?: string): boolean => {
    if (!key) {
        return false;
    }

    if (explicitMoneyFields.has(key)) {
        return true;
    }

    return moneyFieldPattern.test(key);
};

const isYesNoField = (key?: string): boolean => {
    if (!key) {
        return false;
    }

    const normalized = key.replace(/[_\s-]/g, '').toLowerCase();
    const explicitBooleanFields = new Set([
        'existingcustomer',
        'homeaffairsdeceasedstatus',
        'debtreviewstatus'
    ]);

    return (
        normalized.startsWith('has') ||
        normalized.startsWith('is') ||
        normalized.endsWith('yn') ||
        normalized.endsWith('flag') ||
        explicitBooleanFields.has(normalized)
    );
};

const toYesNoValue = (value: unknown): string | null => {
    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }

    if (typeof value === 'number') {
        if (value === 1) {
            return 'Yes';
        }
        if (value === 0) {
            return 'No';
        }
        return null;
    }

    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (!normalized) {
            return null;
        }

        if (['y', 'yes', 'true', '1', 't'].includes(normalized)) {
            return 'Yes';
        }
        if (['n', 'no', 'false', '0', 'f'].includes(normalized)) {
            return 'No';
        }
    }

    return null;
};

const toDisplayString = (value: unknown, key?: string): string => {
    if (value === null || value === undefined || value === '') {
        return '-';
    }

    if (isYesNoField(key)) {
        const yesNoValue = toYesNoValue(value);
        if (yesNoValue) {
            return yesNoValue;
        }
    }

    if (shouldFormatAsCurrency(key)) {
        const numericValue = toNumberValue(value);
        if (numericValue !== null) {
            return formatCurrency(numericValue);
        }
    }

    if (typeof value === 'string') {
        return toVariableDescription(value);
    }

    if (typeof value === 'object') {
        return JSON.stringify(value);
    }

    return String(value);
};

const isPrimitive = (value: unknown): value is string | number | boolean => {
    return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
};

const isRuleRecord = (value: unknown): value is Record<string, unknown> => {
    const record = toRecord(value);
    if (!record) {
        return false;
    }

    return 'rule_type' in record || 'variable' in record || 'rule' in record || 'actual_value' in record;
};

const ruleRowStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.45rem',
    width: '100%'
};

const rulesListStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.45rem',
    width: '100%'
};

const baseRuleChipStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    minHeight: '2rem',
    padding: '0.3rem 0.75rem',
    border: '1px solid #d7e0ec',
    borderRadius: '999px',
    background: '#ffffff',
    color: '#1f2937',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
    whiteSpace: 'nowrap'
};

const ruleTypeChipStyle: React.CSSProperties = {
    ...baseRuleChipStyle,
    background: '#e8f0fb',
    borderColor: '#c6d6ef',
    color: '#20446e',
    fontSize: '0.72rem',
    fontWeight: 800,
    letterSpacing: '0.04em',
    textTransform: 'uppercase'
};

const ruleFieldChipStyle: React.CSSProperties = {
    ...baseRuleChipStyle,
    background: '#f8fafc',
    borderColor: '#dbe3ef',
    color: '#0f172a',
    fontWeight: 700
};

const ruleAccentChipStyle: React.CSSProperties = {
    ...baseRuleChipStyle,
    background: '#ecfeff',
    borderColor: '#99f6e4'
};

const ruleChipLabelStyle: React.CSSProperties = {
    color: '#64748b',
    fontSize: '0.68rem',
    fontWeight: 800,
    letterSpacing: '0.04em',
    textTransform: 'uppercase'
};

const ruleChipValueStyle: React.CSSProperties = {
    color: '#111827',
    fontSize: '0.86rem',
    fontWeight: 700
};

const ruleAccentValueStyle: React.CSSProperties = {
    ...ruleChipValueStyle,
    color: '#0f766e'
};

const tableHeaderCellStyle: React.CSSProperties = {
    fontSize: '0.72rem',
    letterSpacing: '0.01em',
    whiteSpace: 'nowrap',
    borderRight: '1px solid #dbe3ef'
};

const tableBodyCellStyle: React.CSSProperties = {
    borderRight: '1px solid #e2e8f0'
};

const isHiddenXdsField = (key: string): boolean => {
    const normalized = key.replace(/[_\s-]/g, '').toLowerCase();
    return normalized === 'reportid' || normalized === 'xdsreportid';
};

const formatFieldLabel = (key: string): string => {
    const mappedFieldLabel = scoringVariableDescriptions[key];
    if (mappedFieldLabel) {
        return mappedFieldLabel;
    }

    return normalizeKnownAcronyms(key
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .replace(/_/g, ' ')
        .replace(/^./, (char) => char.toUpperCase())
        .trim());
};

const renderNestedRecord = (record: Record<string, unknown>) => {
    const nestedEntries = Object.entries(record).filter(([, nestedValue]) => {
        return nestedValue !== null && nestedValue !== undefined && nestedValue !== '';
    });

    if (nestedEntries.length === 0) {
        return <span>-</span>;
    }

    return (
        <dl className="director-reports-modal__kv-list mb-0">
            {nestedEntries.map(([nestedKey, nestedValue]) => (
                <div key={nestedKey} className="director-reports-modal__kv-item">
                    <dt>{formatFieldLabel(nestedKey)}</dt>
                    <dd>{renderValue(nestedKey, nestedValue)}</dd>
                </div>
            ))}
        </dl>
    );
};

const renderRulesList = (value: unknown) => {
    const rules = toRecordArray(value).filter((rule) => isRuleRecord(rule));
    if (rules.length === 0) {
        return <span>-</span>;
    }

    return (
        <div className="director-reports-modal__rules-list" style={rulesListStyle}>
            {rules.map((rule, index) => {
                const ruleType = toDisplayString(rule.rule_type);
                const variable = toDisplayString(rule.variable);
                const predicate = toDisplayString(rule.rule);
                const actualValue = toDisplayString(rule.actual_value);

                return (
                    <div className="director-reports-modal__rule-item" key={`rule-${index}`} style={ruleRowStyle}>
                        <span className="director-reports-modal__rule-chip director-reports-modal__rule-chip--type" style={ruleTypeChipStyle}>{ruleType}</span>
                        <span className="director-reports-modal__rule-chip director-reports-modal__rule-chip--field" style={ruleFieldChipStyle}>{variable}</span>
                        <span className="director-reports-modal__rule-chip" style={baseRuleChipStyle}>
                            <span className="director-reports-modal__rule-chip-label" style={ruleChipLabelStyle}>Threshold</span>
                            <span className="director-reports-modal__rule-chip-value" style={ruleChipValueStyle}>{predicate}</span>
                        </span>
                        <span className="director-reports-modal__rule-chip director-reports-modal__rule-chip--accent" style={ruleAccentChipStyle}>
                            <span className="director-reports-modal__rule-chip-label" style={ruleChipLabelStyle}>Actual</span>
                            <span className="director-reports-modal__rule-chip-value" style={ruleAccentValueStyle}>{actualValue}</span>
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

const renderValue = (key: string, value: unknown): React.ReactNode => {
    if (key === 'rules_triggered' || key === 'rulesTriggered') {
        return renderRulesList(value);
    }

    if (Array.isArray(value)) {
        if (value.some((item) => isRuleRecord(item))) {
            return renderRulesList(value);
        }

        const items = value.filter((item) => item !== null && item !== undefined && item !== '');
        if (items.length === 0) {
            return <span>-</span>;
        }

        const primitives = items.every((item) => isPrimitive(item));
        if (primitives) {
            return <span>{items.map((item) => toDisplayString(item, key)).join(', ')}</span>;
        }

        return (
            <div className="director-reports-modal__stacked-values">
                {items.map((item, index) => {
                    const itemRecord = toRecord(item);
                    if (itemRecord) {
                        return (
                            <div key={`${key}-${index}`} className="director-reports-modal__nested-card">
                                {renderNestedRecord(itemRecord)}
                            </div>
                        );
                    }

                    return <div key={`${key}-${index}`}>{toDisplayString(item, key)}</div>;
                })}
            </div>
        );
    }

    const nestedRecord = toRecord(value);
    if (nestedRecord) {
        return renderNestedRecord(nestedRecord);
    }

    return <span>{toDisplayString(value, key)}</span>;
};

export const renderObjectTable = (title: string, value: unknown) => {
    const record = toRecord(value);
    if (!record) {
        return null;
    }

    const entries = Object.entries(record).filter(([key, entryValue]) => {
        return !isHiddenXdsField(key) && entryValue !== null && entryValue !== undefined && entryValue !== '';
    });

    if (entries.length === 0) {
        return null;
    }

    return (
        <div className="card border-0 shadow-sm mb-3">
            <div className="card-header bg-light">
                <h6 className="mb-0 fw-semibold">{title}</h6>
            </div>
            <div className="card-body p-0">
                <div className="table-responsive">
                    <table className="table table-sm mb-0 align-middle">
                        <tbody>
                            {entries.map(([key, entryValue]) => (
                                <tr key={key}>
                                    <th className="text-muted ps-3" style={{ width: '28%' }}>
                                        {formatFieldLabel(key)}
                                    </th>
                                    <td className="pe-3">{renderValue(key, entryValue)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export const renderArrayTable = (title: string, value: unknown) => {
    const items = toRecordArray(value);
    if (items.length === 0) {
        return null;
    }

    const keys = Array.from(
        new Set(
            items.flatMap((item) => Object.keys(item)).filter((key) => {
                return !isHiddenXdsField(key) && items.some((item) => item[key] !== null && item[key] !== undefined && item[key] !== '');
            })
        )
    );

    if (keys.length === 0) {
        return null;
    }

    return (
        <div className="card border-0 shadow-sm mb-3">
            <div className="card-header bg-light">
                <h6 className="mb-0 fw-semibold">{title}</h6>
            </div>
            <div className="card-body p-0">
                <div className="table-responsive">
                    <table className="table table-sm mb-0 align-middle">
                        <thead>
                            <tr>
                                {keys.map((key) => (
                                    <th key={key} className="small text-muted" style={tableHeaderCellStyle}>
                                        {formatFieldLabel(key)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, rowIndex) => (
                                <tr key={`${title}-${rowIndex}`}>
                                    {keys.map((key) => (
                                        <td key={`${title}-${rowIndex}-${key}`} style={tableBodyCellStyle}>
                                            {toDisplayString(item[key], key)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export const getStatusBadgeClass = (status: DirectorReportView['status']): string => {
    if (status === 'SUCCESS') return 'bg-success';
    if (status === 'FAILED') return 'bg-danger';
    if (status === 'PARTIAL') return 'bg-warning text-dark';
    return 'bg-secondary';
};
